import React, { useState } from 'react';
import { InfoAlert } from 'src/components/atoms/alerts/InfoAlert';
import { DataBox, DataBoxLegendProps } from 'src/components/molecules/DataBox';
import { NewAreaGraph } from 'src/components/molecules/graphs/NewAreaGraph';

type GraphDataPoint = { utcEpoch: number } & Record<
  string,
  number | null | undefined
>;

type KeyMetricGraphBoxProps = {
  title: string;
  data: GraphDataPoint[];
  xAxisFormatter: (value: number | string) => string;
  yAxisFormatter: (value: number | string) => string;
  isError: boolean;
  isLoading: boolean;
  // Series label for the default single-series legend/tooltip
  legendLabel?: string;
  // Full legend override for multi-series graphs
  legendProps?: DataBoxLegendProps;
  // Optional info alert behind the box's info button
  infoText?: string;
  // Info alert header when it differs from the box title
  infoHeader?: string;
  // Optional "view as table" modal: the owner keeps the modal node and state,
  // onTableModalOpen is forwarded to the DataBox table button
  tableModal?: React.ReactNode;
  onTableModalOpen?: (isOpen: boolean) => void;
  exportEnabled?: boolean;
};

// Key metric graphs always render 1 per row on the 12-column page grid
const COL_SPAN = 12;
const MIN_H = 400;

export const KeyMetricGraphBox: React.FC<KeyMetricGraphBoxProps> = ({
  title,
  data,
  xAxisFormatter,
  yAxisFormatter,
  isError,
  isLoading,
  legendLabel,
  legendProps,
  infoText,
  infoHeader,
  tableModal,
  onTableModalOpen,
  exportEnabled,
}) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const resolvedLegendProps: DataBoxLegendProps = legendProps ?? {
    placement: 'none',
    baseLegendKeys: ['value'],
    ...(legendLabel ? { labelMap: { value: legendLabel } } : {}),
  };

  return (
    <>
      {infoText && (
        <InfoAlert
          headerText={infoHeader ?? title}
          bodyText={infoText}
          isOpen={infoOpen}
          onClose={() => setInfoOpen(false)}
        />
      )}
      <DataBox
        title={title}
        colSpan={COL_SPAN}
        minH={MIN_H}
        onInfoButtonClick={infoText ? () => setInfoOpen(true) : undefined}
        legendProps={resolvedLegendProps}
        exportEnabled={exportEnabled}
        exportButtonEnabled={exportEnabled ? false : undefined}
        setTableDataModalIsOpen={onTableModalOpen}
        hasData={data.length > 0}
        isError={isError}
        isLoading={isLoading}>
        <>
          {tableModal}
          <NewAreaGraph
            data={data}
            xAxisKey="utcEpoch"
            xAxisFormatter={xAxisFormatter}
            yAxisFormatter={yAxisFormatter}
          />
        </>
      </DataBox>
    </>
  );
};
